import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gift, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { z } from 'zod';

const emailSchema = z.string().email();

export function ExitIntentPopup() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (e.clientY <= 0 && !sessionStorage.getItem('exit-popup-shown') && !isAuthenticated) {
      setOpen(true);
      sessionStorage.setItem('exit-popup-shown', '1');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated || sessionStorage.getItem('exit-popup-shown')) return;
    // Delay 10s before listening
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 10000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave, isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast.error(t('landing.newsletter.invalidEmail', 'Please enter a valid email'));
      return;
    }
    setStatus('loading');
    try {
      const { error } = await supabase
        .from('newsletter_subscribers' as any)
        .insert({ email: email.toLowerCase().trim(), source: 'exit_intent' } as any);
      if (error && error.code !== '23505') throw error;
      setStatus('success');
      setTimeout(() => setOpen(false), 2000);
    } catch {
      toast.error(t('landing.newsletter.error', 'Something went wrong.'));
      setStatus('idle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Gift className="h-5 w-5 text-[#4F6CFF]" />
            {t('landing.exitIntent.title', 'Before you go… want a free diagnostic?')}
          </DialogTitle>
        </DialogHeader>
        {status === 'success' ? (
          <p className="text-center py-4 text-sm text-muted-foreground">
            {t('landing.exitIntent.success', 'Done! You\'ll receive your RevOps checklist soon.')}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {t('landing.exitIntent.description', 'Leave your email and get a complete RevOps checklist to evaluate your revenue operation maturity.')}
            </p>
            <form onSubmit={handleSubmit} className="flex gap-2 mt-2">
              <Input
                type="email"
                placeholder={t('landing.newsletter.placeholder', 'Your best email')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <Button type="submit" disabled={status === 'loading'} className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white whitespace-nowrap">
                {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('landing.exitIntent.cta', 'Get the Checklist')}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
